import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../config';
import * as types from '../../lib/constants';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  Header,
  Contents,
  CloseButton,
  CloseButtonImage,
  UpdateButton,
  CancelButton,
} from '../../styles/common/Dialog';
import {
  Dialog,
  Submit,
  InfoContents,
  BaseInfo,
  AccountInfo,
  InfoTitle,
  AccountInfoBody,
  SelectBox,
  ErrorMessage,
} from './styles/StaffDetailDialogStyles';
import CustomInput from '../Parts/CustomInput';
import {
  ROLE,
  REMOTE_SUPPORT_STATUS,
  ACCOUNT_STATUS,
} from '../../lib/constants';
import ErrorMsgArea from '../../styles/common/ErrorMessage';
import createSelectOptions from './functions/createSelectOptions';
import useStaffActions from './hooks/useStaffActions';
import createOperator from '../../actions/api/operators/createOperator';

const CreateStaffDialog = ({ onCloseCreateStaffDialog }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const dispatch = useDispatch();
  const apiToken = useSelector(state => state.main.apiToken);
  const confirmData = useSelector(state => state.operator.confirmData);
  const allRoles = [ROLE.GUEST, ROLE.GENERAL, ROLE.ADMIN, ROLE.COMPANY_ADMIN];

  // 入力ボックスのプレースホルダ
  const userNamePlaceholder = intl.formatMessage({
    id: 'staff.createDialog.input.userName.placeholder',
  });
  const emailPlaceholder = intl.formatMessage({
    id: 'staff.createDialog.input.email.placeholder',
  });

  const {
    handleRoleSelect,
    handleStatusSelect,
    handleRemoteSupportStatusSelect,
  } = useStaffActions(dispatch);

  // フォームバリデーションのスキーマ
  const schema = z.object({
    userName: z.string().min(1),
    email: z.string().min(1).email(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  // 追加ボタン押下時に実行
  const onSubmit = data => {
    const isRemoteSupport =
      confirmData.remoteSupportStatus === REMOTE_SUPPORT_STATUS.enable;
    const isActiveUser = confirmData.accountStatus === ACCOUNT_STATUS.enable;
    const apiParams = {
      email: data.email,
      name: data.userName,
      role: confirmData.role,
      license: {
        isActiveUser,
        isRemoteSupport,
      },
    };
    dispatch(createOperator(apiToken, apiParams));
    onCloseCreateStaffDialog();
  };

  // エラーメッセージ
  const updateOperatorsError = confirmData.updateOperatorsError || {};
  const updateErrorMessageId = updateOperatorsError.userLicense
    ? `operator.updateStaff.message.error.accountStatus.${updateOperatorsError.errorCode}`
    : `operator.updateStaff.message.error.remoteSupportStatus.${updateOperatorsError.errorCode}`;

  useEffect(() => {
    handleRoleSelect(ROLE.GUEST);
    handleRemoteSupportStatusSelect(REMOTE_SUPPORT_STATUS.enable);
    handleStatusSelect(ACCOUNT_STATUS.enable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Main id="closeConfirmStaffWindow" role="presentation">
      <Dialog role="presentation">
        <Contents>
          <Header>
            <FormattedMessage id="staff.createDialog.title" />
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={onCloseCreateStaffDialog}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </Header>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InfoContents>
              <AccountInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.accountName" />
                </InfoTitle>
                <AccountInfoBody>
                  <CustomInput
                    id="userName"
                    placeholder={userNamePlaceholder}
                    defaultValue=""
                    register={register('userName', {
                      required: true,
                      maxLength: 255,
                    })}
                  />
                </AccountInfoBody>
              </AccountInfo>
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.mailAddress" />
                </InfoTitle>
                <AccountInfoBody>
                  <CustomInput
                    id="email"
                    defaultValue=""
                    placeholder={emailPlaceholder}
                    register={register('email', {
                      required: true,
                    })}
                  />
                </AccountInfoBody>
              </BaseInfo>
              {/* 権限項目 */}
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.role" />
                </InfoTitle>
                <AccountInfoBody>
                  <SelectBox
                    id="role"
                    value={confirmData.role}
                    onChange={e => handleRoleSelect(e.target.value)}
                  >
                    {createSelectOptions(
                      allRoles,
                      formatMessage,
                      'operator.role.status',
                    )}
                  </SelectBox>
                </AccountInfoBody>
              </BaseInfo>
              {/* ライセンス項目 */}
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.license" />
                </InfoTitle>
                <AccountInfoBody>
                  <SelectBox
                    id="selectUpdateAccountStatus"
                    value={confirmData.accountStatus}
                    onChange={e => handleStatusSelect(e.target.value)}
                  >
                    {createSelectOptions(
                      config.searchAccountStatus.notDeleted,
                      formatMessage,
                      'operator.accountStatus',
                    )}
                  </SelectBox>
                  {updateOperatorsError.userLicense && (
                    <ErrorMessage>
                      <FormattedMessage id={updateErrorMessageId} />
                    </ErrorMessage>
                  )}
                </AccountInfoBody>
              </BaseInfo>
              {/* 遠隔支援項目 */}
              <BaseInfo>
                <InfoTitle>
                  <FormattedMessage id="operator.OperatorLists.title.remoteSupportLicense" />
                </InfoTitle>
                <AccountInfoBody>
                  <SelectBox
                    id="selectUpdateRemoteSupportStatus"
                    value={confirmData.remoteSupportStatus}
                    onChange={e =>
                      handleRemoteSupportStatusSelect(e.target.value)
                    }
                  >
                    {createSelectOptions(
                      types.REMOTE_SUPPORT_STATUS_ALL,
                      formatMessage,
                      'operator.remoteSupportStatus',
                    )}
                  </SelectBox>
                  {updateOperatorsError.remoteSupportLicense && (
                    <ErrorMessage>
                      <FormattedMessage id={updateErrorMessageId} />
                    </ErrorMessage>
                  )}
                </AccountInfoBody>
              </BaseInfo>
            </InfoContents>
            {/* エラー表示 */}
            {(errors.userName?.type === 'too_small' ||
              errors.email?.type === 'too_small') && (
              <ErrorMsgArea>
                <FormattedMessage id="staff.createDialog.input.tooSmall.errorMessage" />
              </ErrorMsgArea>
            )}
            {errors.email?.type === 'invalid_string' && (
              <ErrorMsgArea>
                <FormattedMessage id="staff.createDialog.input.invalidString.errorMessage" />
              </ErrorMsgArea>
            )}
            {/* ボタングループ */}
            <Submit>
              <CancelButton onClick={onCloseCreateStaffDialog}>
                <FormattedMessage id="common.button.cancel" />
              </CancelButton>
              <UpdateButton
                id="updateButtonClick"
                role="presentation"
                className="button"
                type="submit"
              >
                <FormattedMessage id="common.button.add" />
              </UpdateButton>
            </Submit>
          </form>
        </Contents>
      </Dialog>
    </Main>
  );
};

CreateStaffDialog.propTypes = {
  onCloseCreateStaffDialog: PropTypes.func.isRequired,
};

export default CreateStaffDialog;
